import { CallingTheme, DevicesButtonStyles } from "@azure/communication-react";
import { IRawStyle, PartialTheme, memoizeFunction, IButtonStyles, Theme } from '@fluentui/react';
import { buttonFlyoutIncreasedSizeStyles } from "./Button.styles";
import { pcpGrayPalette, pcpRedPalette, pcpBluePalette, pcpYellowPalette } from "./themes/palette";

export type ButtonSize = 'small' | 'medium';
const SMALL_ICON_SIZE = '2.625rem'; //=='42px'
const MEDIUM_ICON_SIZE = '3rem';    //=='48px'
export const svgIconStyle = (theme: Theme, disabled?: boolean) => ({
    width: 24,
    height: 24,
    fill: disabled ? theme.palette.neutralTertiary : theme.palette.neutralPrimaryAlt,

    '@media (forced-colors: active) and (prefers-color-scheme: dark)': {
        fill: disabled ? theme.palette.neutralPrimaryAlt : theme.palette.neutralTertiary
    }
});
export function devicesButtonStylesMobile(theme: PartialTheme & CallingTheme):DevicesButtonStyles {
    return {
        ...controlBarButtonStyles(theme),
        menuStyles: {
            menuItemStyles: buttonFlyoutIncreasedSizeStyles
        }
    }
}

export function controlBarButtonStyles(theme: PartialTheme & CallingTheme, size: ButtonSize = 'medium') {
    return {
        root : {
            boxShadow: '0px 2px 10px 0px #00000026',
            background: '#75839D',
            color: theme.callingPalette.iconWhite,
            minWidth: size === 'small'  ? SMALL_ICON_SIZE : MEDIUM_ICON_SIZE,
            minHeight: size === 'small'  ? SMALL_ICON_SIZE : MEDIUM_ICON_SIZE,
            padding: 0,
            borderRadius: '50%',

            '@media (forced-colors: active)': {
                ':focus': {
                    border: '1px solid !important' // we need bang important to override fluents colours
                },
                ':focus::after': {
                    border: 'unset !important', // we need bang important to override fluents colours
                    outline: 'unset !important'
                }
            }
        },
        rootChecked: {
            background: `linear-gradient(270deg, ${pcpBluePalette.foregroundDark} 0%, ${pcpBluePalette.foregroundDarkAlternative} 100%)`,
            color: theme.callingPalette.iconWhite,
        },
        rootDisabled: {
            background: ' #D2D7DF',
            color: theme.callingPalette.iconWhite
        },
        rootCheckedDisabled: {
            background: ' #D2D7DF',
        },
        rootHovered: {
            background: ' #8B9CBB',
            color: theme.callingPalette.iconWhite,
        },
        rootCheckedHovered: {
            color: theme.callingPalette.iconWhite,
            background: 'linear-gradient(270deg, #2F97FA 0%, #8B89FB 100%)',    //"ScreenShareButton" usecase
        },
        rootPressed: {
            color: theme.callingPalette.iconWhite,
        },
        rootExpanded: {
            color: theme.callingPalette.iconWhite,
            background: `linear-gradient(270deg, ${pcpBluePalette.foregroundDark} 0%, ${pcpBluePalette.foregroundDarkAlternative} 100%)`,
        }
    } as IButtonStyles;
}

export function fullScreenButtonStyles(theme: PartialTheme & CallingTheme, size?: ButtonSize) {
    const baseStyles =  controlBarButtonStyles(theme, size);
    const rootChecked = baseStyles.rootChecked as IRawStyle;
    const rootCheckedHovered = baseStyles.rootCheckedHovered as IRawStyle;
    return {
        ...baseStyles,
        root: {
            ...(baseStyles.root || {}) as object,
            width: size === 'small'  ? SMALL_ICON_SIZE : MEDIUM_ICON_SIZE,
            zIndex: 1,    //should be above "#active-element" element
            background: rootChecked?.background || pcpBluePalette.foregroundDark,
            border: 'none', //reset border of "DefaultButton" component
        },
        rootHovered: {
            background: rootCheckedHovered?.background || pcpBluePalette.foregroundDark
        },
        flexContainer: {
            display: 'flex',
            height: 'auto',
        }
    } as IButtonStyles;
}

export const endCallButtonStyles = memoizeFunction((theme: PartialTheme & CallingTheme, size?: ButtonSize) => {
    const controlBarButton = controlBarButtonStyles(theme, size);
    return {
        root: {
            ...(controlBarButton.root || {}) as object,
            background: `linear-gradient(270deg, ${pcpRedPalette.foregroundDark} 0%, ${pcpRedPalette.foregroundDarkAlternative} 100%)`,
        },
        rootHovered: {
            ...(controlBarButton.rootHovered || {}) as object,
            background: `linear-gradient(270deg, ${pcpRedPalette.foregroundLight} 0%, ${pcpRedPalette.foregroundLightAlternative} 100%)`,
        },
        rootPressed: {
            background: ` ${pcpRedPalette.foregroundDarker} `,
        },
        rootDisabled: {
            background: pcpGrayPalette.foreground
        },
        rootCheckedDisabled: {
            background: pcpGrayPalette.foreground
        }
    }  as IButtonStyles;
});

export function raiseHandButtonStyles(theme: PartialTheme & CallingTheme, size?: ButtonSize) {
    const controlBarButton = controlBarButtonStyles(theme, size);
    return {
        ...controlBarButton,
        root : {
            ...(controlBarButton.root || {}) as object,
            background: `linear-gradient(270deg, ${pcpBluePalette.foregroundDark} 0%, ${pcpBluePalette.foregroundDarkAlternative} 100%)`,
        },
        rootHovered: {
            ...(controlBarButton.rootHovered || {}) as object,
            background: `linear-gradient(270deg, ${pcpBluePalette.foregroundLight} 0%, ${pcpBluePalette.foregroundLightAlternative} 100%)`,
        },
        rootChecked: {
            ...(controlBarButton.rootChecked || {}) as object,
            background: `linear-gradient(180deg, ${pcpYellowPalette.foregroundDark} 0%, ${pcpYellowPalette.foregroundDarkAlternative} 100%)`,
        },
        rootCheckedHovered: {
            ...(controlBarButton.rootCheckedHovered || {}) as object,
            background: `linear-gradient(180deg, ${pcpYellowPalette.foregroundLight} 0%, ${pcpYellowPalette.foregroundLightAlternative} 100%)`, //guessed colors for hover effect
        },
    }  as IButtonStyles;
}

export function pageButtonStyles(theme: PartialTheme & CallingTheme) {
    const controlBarButton = controlBarButtonStyles(theme);
    return {
        ...controlBarButton,
        root: {
            ...(controlBarButton.root || {}) as object ,
            visibility: 'visible'
        },
        rootDisabled: {
            ...(controlBarButton.rootDisabled || {}) as object,
            visibility: 'hidden'
        },
    }  as IButtonStyles;
}

export const controlBarStyles = (size: 'small' | 'medium' | undefined = 'medium', visibility: 'hidden' | 'visible' = 'visible') => ({
    root: {
        display: 'flex',
        visibility: visibility,
        justifyContent: 'center',
        gap: size == 'small' ? 
            '0.5rem' //8px
            : '1.25rem', //20px
        marginBottom: '1.5rem',  //almost 25px
        marginTop: '1.5rem',  //almost 25px
        background: 'transparent',  //overrides default color from "theme.palette.white"
        boxShadow: 'none',
        overflowX: 'visible',    //prevents button box-shadow being cut
        overflowY: 'visible',    //prevents button box-shadow being cut
    }
})